import * as React from "react";
import { DEFAULT_ENV_NAME } from "environmentVariables/model/EnvironmentState";
import { DEFAULT_AGENT_GROUP_NAME } from "processList/actions/DeployActions";

// --- Event payload definitions ---
interface EnvironmentChangeEvent {
    environmentName: string;
    agentGroupName?: string;
}

interface SearchParametersChangeEvent {
    searchParams: URLSearchParams;
}

// Register all custom event types here
interface FrendsNavEventMap {
    frendsEnvironmentChange: EnvironmentChangeEvent;
    frendsSearchParametersChange: SearchParametersChangeEvent;
}

type FrendsNavEventName = keyof FrendsNavEventMap;

// --- Default values per event type ---
const EVENT_DEFAULTS: { [K in FrendsNavEventName]: () => FrendsNavEventMap[K] } = {
    frendsEnvironmentChange: () => ({
        environmentName: DEFAULT_ENV_NAME,
        agentGroupName: DEFAULT_AGENT_GROUP_NAME,
    }),
    frendsSearchParametersChange: () => ({
        searchParams: new URLSearchParams(),
    }),
};

export const useFrendsNavEvent = <T extends FrendsNavEventName>(
    eventName: T,
    callback?: (data: FrendsNavEventMap[T]) => void
) => {
    React.useEffect(() => {
        if (!callback) {
            return;
        }

        const onEvent = (event: Event) => {
            const customEvent = event as CustomEvent<FrendsNavEventMap[T]>;
            if (customEvent.detail) {
                callback(customEvent.detail);
            }
        };

        window.addEventListener(eventName, onEvent);

        return () => {
            window.removeEventListener(eventName, onEvent);
        };
    }, [eventName, callback]);

    const dispatch = (detail?: Partial<FrendsNavEventMap[T]>) => {
        const defaults = EVENT_DEFAULTS[eventName]();
        const event = new CustomEvent(eventName, {
            detail: { ...defaults, ...detail } as FrendsNavEventMap[T],
        });
        window.dispatchEvent(event);
    };

    return { dispatch };
};
